import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { questions } from "@/data/questions";
import { QuestionCard } from "@/components/assessment/QuestionCard";
import { ProgressBar } from "@/components/assessment/ProgressBar";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, Home } from "lucide-react";

export interface Answer {
  questionId: number;
  value: number;
}

const Assessment = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [activeRun, setActiveRun] = useState<any>(null);
  const [hasAttempted, setHasAttempted] = useState(false);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    checkActiveRunAndAttempt();
  }, [user]);

  const checkActiveRunAndAttempt = async () => {
    setLoading(true);

    const { data: run } = await supabase
      .from("assessment_runs")
      .select("*")
      .eq("is_active", true)
      .maybeSingle();

    if (!run) {
      setLoading(false);
      return;
    }

    setActiveRun(run);

    const { data: attempt } = await supabase
      .from("attempts")
      .select("*")
      .eq("user_id", user?.id)
      .eq("run_id", run.id)
      .order("started_at", { ascending: false }) // Get most recent attempt
      .maybeSingle();

    console.log('[Assessment Debug] Active Run:', run.id);
    console.log('[Assessment Debug] Found attempt:', attempt ? attempt.id : 'none');

    if (attempt) {
      if (attempt.completed) {
        // User has already completed this assessment
        console.log('[Assessment Debug] Attempt is completed.');
        setHasAttempted(true);
      } else {
        // User has an incomplete attempt - let them continue
        console.log('[Assessment Debug] Resuming incomplete attempt:', attempt.id);
        setAttemptId(attempt.id);

        // Load their previous responses
        const { data: existingResponses } = await supabase
          .from("responses")
          .select("question_id, rating")
          .eq("attempt_id", attempt.id);

        console.log('[Assessment Debug] Existing responses count:', existingResponses?.length);

        if (existingResponses && existingResponses.length > 0) {
          const loadedAnswers: Answer[] = existingResponses.map(r => ({
            questionId: parseInt(r.question_id),
            value: r.rating
          }));
          setAnswers(loadedAnswers);

          // Find the first unanswered question
          const answeredQuestionIds = new Set(loadedAnswers.map(a => a.questionId));
          const firstUnansweredIndex = questions.findIndex(q => !answeredQuestionIds.has(q.id));

          console.log('[Assessment Debug] First unanswered index:', firstUnansweredIndex);

          if (firstUnansweredIndex !== -1) {
            setCurrentQuestionIndex(firstUnansweredIndex);
          } else {
            // All questions answered but not marked complete?
            // Maybe go to last question or review?
            setCurrentQuestionIndex(questions.length - 1);
          }
        } else {
          console.log('[Assessment Debug] No responses found, starting at Q1');
          // No responses, start from the beginning
          setAnswers([]);
          setCurrentQuestionIndex(0);
        }
      }
    } else {
      // No attempt exists - create new one
      console.log('[Assessment Debug] No attempt found. Creating new one.');
      const { data: newAttempt, error } = await supabase
        .from("attempts")
        .insert({
          user_id: user?.id,
          run_id: run.id,
          completed: false,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // Unique constraint violation - attempt already exists
          console.log('[Assessment Debug] Attempt already exists (unique constraint). Fetching existing...');
          const { data: existing } = await supabase
            .from("attempts")
            .select("*")
            .eq("user_id", user?.id)
            .eq("run_id", run.id)
            .maybeSingle();

          if (existing) {
            if (existing.completed) {
              // Attempt exists but is completed
              console.log('[Assessment Debug] Existing attempt is completed.');
              setHasAttempted(true);
            } else {
              // Attempt exists and is in progress - resume it
              console.log('[Assessment Debug] Resuming existing incomplete attempt:', existing.id);
              setAttemptId(existing.id);

              const { data: existingResponses } = await supabase
                .from("responses")
                .select("question_id, rating")
                .eq("attempt_id", existing.id);

              if (existingResponses && existingResponses.length > 0) {
                const loadedAnswers: Answer[] = existingResponses.map(r => ({
                  questionId: parseInt(r.question_id),
                  value: r.rating
                }));
                setAnswers(loadedAnswers);
                const answeredQuestionIds = new Set(loadedAnswers.map(a => a.questionId));
                const firstUnansweredIndex = questions.findIndex(q => !answeredQuestionIds.has(q.id));
                if (firstUnansweredIndex !== -1) {
                  setCurrentQuestionIndex(firstUnansweredIndex);
                } else {
                  setCurrentQuestionIndex(questions.length - 1);
                }
              } else {
                setAnswers([]);
                setCurrentQuestionIndex(0);
              }
            }
          }
        } else {
          console.error("Error creating attempt:", error);
          toast({ title: "Error", description: "Failed to start assessment. Please try again.", variant: "destructive" });
        }
      } else if (newAttempt) {
        setAttemptId(newAttempt.id);
        console.log('New attempt created:', newAttempt.id);
        // Start with empty answers
        setAnswers([]);
        setCurrentQuestionIndex(0);
      }
    }

    setLoading(false);
  };

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = answers.find(a => a.questionId === currentQuestion.id);

  const handleAnswer = (value: number) => {
    setAnswers(prev => {
      const existing = prev.find(a => a.questionId === currentQuestion.id);
      if (existing) {
        return prev.map(a =>
          a.questionId === currentQuestion.id ? { ...a, value } : a
        );
      }
      return [...prev, { questionId: currentQuestion.id, value }];
    });
  };

  const saveCurrentResponse = async () => {
    const currentAnswer = answers.find(a => a.questionId === currentQuestion.id);
    if (!currentAnswer || !attemptId) return;

    console.log('[Assessment Debug] Saving response for Q:', currentQuestion.id, 'via Backend Proxy');

    try {
      const response = await fetch("/auth/ldap/user/save-response", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user?.id,
          attemptId: attemptId,
          questionId: currentQuestion.id,
          competenceArea: currentQuestion.area,
          rating: currentAnswer.value
        }),
      });

      const data = await response.json();

      if (!data.success) {
        console.error('[Assessment Debug] Proxy Save Failed:', data.message);
        toast({ title: "Warning", description: "Could not save progress: " + data.message, variant: "destructive" });
      } else {
        console.log('[Assessment Debug] Saved successfully via Proxy.');
      }
    } catch (e: any) {
      console.error('[Assessment Debug] Proxy Network Error:', e);
      toast({ title: "Error", description: "Network error saving progress.", variant: "destructive" });
    }
  };

  const handleNext = async () => {
    // Save progress before moving to next question
    await saveCurrentResponse();

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      await completeAssessment();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const completeAssessment = async () => {
    if (!attemptId || !user?.id) return;

    const calculateOverallScore = () => {
      const totalScore = answers.reduce((sum, a) => sum + a.value, 0);
      const maxScore = questions.length * 8;
      const minScore = questions.length * 1;
      const normalizedScore = ((totalScore - minScore) / (maxScore - minScore)) * 100;
      return Math.max(0, Math.min(100, Math.round(normalizedScore)));
    };

    const overallScore = calculateOverallScore();

    console.log('[Assessment Debug] Completing assessment via Backend Proxy');

    try {
      const response = await fetch("/auth/ldap/user/complete-assessment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: user.id,
          attemptId: attemptId,
          overallScore: overallScore
        }),
      });

      const data = await response.json();

      if (!data.success) {
        console.error('[Assessment Debug] Completion Failed:', data.message);
        toast({ title: "Error", description: "Failed to complete assessment: " + data.message, variant: "destructive" });
        return;
      }

      toast({ title: "Success", description: "Assessment completed successfully!" });
      navigate("/dashboard");

    } catch (error) {
      console.error('[Assessment Debug] Completion Error:', error);
      toast({ title: "Error", description: "Failed to complete assessment: " + error.message, variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!activeRun) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center px-4">
        <Card className="p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">No Active Assessment</h2>
          <p className="text-muted-foreground mb-6">
            There is currently no active assessment run. Please contact your administrator.
          </p>
          <Button onClick={() => navigate("/")} className="gap-2">
            <Home className="h-4 w-4" />
            Go Home
          </Button>
        </Card>
      </div>
    );
  }

  if (hasAttempted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary flex items-center justify-center px-4">
        <Card className="p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">Already Completed</h2>
          <p className="text-muted-foreground mb-6">
            You have already completed this assessment run. Check your dashboard to see your results.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/")} variant="outline" className="gap-2">
              <Home className="h-4 w-4" />
              Home
            </Button>
            <Button onClick={() => navigate("/dashboard")}>View Dashboard</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(210,30%,15%)] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Bilingual Instructions */}
        <Card className="p-4 md:p-6 mb-6 bg-primary/5 border-primary/20">
          <div className="space-y-3">
            {attemptId && (
              <div className="mb-4 p-3 md:p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-sm md:text-base text-blue-200 font-semibold flex items-start gap-2">
                  <span className="text-lg">ℹ️</span>
                  <span>You have an in-progress assessment. Please complete all questions to submit your survey.</span>
                </p>
                <p className="text-sm md:text-base text-blue-200/80 font-semibold font-tajawal mt-2 flex items-start gap-2" dir="rtl">
                  <span className="text-lg">ℹ️</span>
                  <span>لديك تقييم قيد التقدم. يرجى إكمال جميع الأسئلة لتقديم الاستبيان الخاص بك.</span>
                </p>
              </div>
            )}
            <p className="text-sm md:text-xl font-semibold text-gray-100 leading-relaxed">
              <strong className="text-primary text-base md:text-2xl">Instructions:</strong> Answer all 24 questions by rating your competence level from 1 (Basic) to 5 (Advanced). Take your time and answer honestly for accurate results.
            </p>
            <p className="text-sm md:text-xl font-semibold text-gray-100 font-tajawal leading-relaxed" dir="rtl">
              <strong className="text-primary text-base md:text-2xl">التعليمات:</strong> أجب عن جميع الأسئلة الـ 24 من خلال تقييم مستوى كفاءتك من 1 (أساسي) إلى 5 (متقدم). خذ وقتك وأجب بصدق للحصول على نتائج دقيقة.
            </p>
          </div>
        </Card>

        <div className="mb-8">
          <QuestionCard
            question={currentQuestion}
            selectedValue={currentAnswer?.value || null}
            onSelect={handleAnswer}
            onContinue={handleNext}
            canContinue={!!currentAnswer}
          />
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center items-center gap-6 text-gray-400">
          <button
            onClick={handlePrevious}
            disabled={currentQuestionIndex === 0}
            className="p-3 bg-[hsl(210,25%,25%)] rounded-full hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-[hsl(210,25%,35%)] shadow-lg"
            aria-label="Previous question"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <div className="flex items-center gap-3">
            <span className="text-3xl font-light text-gray-100 min-w-[1.5em] text-center">
              {currentQuestionIndex + 1}
            </span>
            <span className="text-2xl text-gray-500">/</span>
            <span className="text-xl text-gray-400">{questions.length}</span>
          </div>

          <button
            onClick={handleNext}
            disabled={!currentAnswer}
            className="p-3 bg-[hsl(210,25%,25%)] rounded-full hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors border border-[hsl(210,25%,35%)] shadow-lg"
            aria-label="Next question"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mt-6">
          <ProgressBar
            current={currentQuestionIndex + 1}
            total={questions.length}
          />
        </div>
      </div>
    </div>
  );
};

export default Assessment;
